/**
 * The address and phone standards (docs/02 "The address standard", "The phone
 * standard"; migration 0040).
 *
 * ONE shape for every address and every phone number in PropOS. Matt,
 * 2026-08-24: "this should be the standardized address format for all addresses
 * in the system", "we should standardize all phone numbers and addresses as
 * well", and "both of these standardized formats should be documented so this
 * isn't an issue moving forward." docs/02 is the prose; this file is the
 * enforcement, and the two must agree.
 *
 * Addresses and phones are SUB-RESOURCES rather than fields on their parent —
 * the same shape `notes` and `document_associations` already use — because a
 * record may hold any number of them and each carries its own purpose, primary
 * flag and lifecycle.
 *
 * 0035's comment here read: "Deliberately NOT a polymorphic contact-points
 * table … five columns people fill in beat a join nobody can query." That was
 * right for ONE address and stops being right at N, and v1's own schema is the
 * evidence: its vendor PHONES had already been migrated off three fixed slots
 * onto a labeled {purpose, number} list "supporting any number of entries".
 * TKC's team hit the ceiling of fixed slots and solved it; addresses were the
 * same problem one step behind.
 */

import { z } from 'zod';

import { partialUpdateSchema } from './partial-update.ts';

/** Everything an address can hang off (0040 CHECK, verbatim). */
export const ADDRESS_ENTITY_TYPES = [
  'property',
  'property_group',
  'vendor',
  'vendor_contact',
  'tenant_org',
  'tenant_contact',
  'internal_contact',
] as const;
export type AddressEntityType = (typeof ADDRESS_ENTITY_TYPES)[number];

/** Phones reach one record addresses do not: a utility account has its own. */
export const PHONE_ENTITY_TYPES = [...ADDRESS_ENTITY_TYPES, 'utility_account'] as const;
export type PhoneEntityType = (typeof PHONE_ENTITY_TYPES)[number];

// ---------------------------------------------------------------------------
// The address standard
// ---------------------------------------------------------------------------

/**
 * The seven components, and no others.
 *
 * `streetNumber` is a STRING: "12-A" and "1/2" are real street numbers and an
 * integer would refuse them. `streetSuffix` is FREE TEXT (Matt's call,
 * 2026-08-24) because USPS publishes ~200 standard suffixes — a picklist of the
 * common dozen refuses a real address the first time somebody types "Trace",
 * and a picklist of 200 is worse to use than typing three letters. The UI offers
 * a datalist of the common ones and accepts anything.
 */
export const addressComponents = {
  streetNumber: z.string().trim().max(30).nullish(),
  streetName: z.string().trim().max(200).nullish(),
  streetSuffix: z.string().trim().max(40).nullish(),
  addressLine2: z.string().trim().max(200).nullish(),
  city: z.string().trim().max(120).nullish(),
  state: z.string().trim().max(40).nullish(),
  postalCode: z.string().trim().max(20).nullish(),
};

/** The editable half. Entity and purpose are chosen once, at creation. */
export const addressEditableSchema = z.object({
  purposeId: z.uuid(),
  ...addressComponents,
  /** The role a notice clause names — "Attn: General Counsel". */
  attention: z.string().trim().max(200).nullish(),
  isPrimary: z.boolean().default(false),
  notes: z.string().trim().max(2000).nullish(),
});

/** Ditto for an address (the components plus purpose, attention and primary). */
export type AddressEditable = z.infer<typeof addressEditableSchema>;

/**
 * An address with nothing in it is not an address, and the database says so too
 * (0040's `addresses_not_empty`). Refusing here as well means the person sees a
 * sentence instead of a constraint violation.
 *
 * Named, and applied in BOTH places an address arrives — its own POST and the
 * inline slot on a contact create — because the second one arriving unrefined is
 * how a purpose-only address block reached the CHECK and came back a 500.
 */
const ADDRESS_NOT_EMPTY = 'An address needs at least a street, a city, a ZIP or an attention line';

export function addressHasContent(value: {
  readonly streetNumber?: string | null | undefined;
  readonly streetName?: string | null | undefined;
  readonly addressLine2?: string | null | undefined;
  readonly city?: string | null | undefined;
  readonly postalCode?: string | null | undefined;
  readonly attention?: string | null | undefined;
}): boolean {
  return [
    value.streetNumber,
    value.streetName,
    value.addressLine2,
    value.city,
    value.postalCode,
    value.attention,
  ].some(filled);
}

/**
 * True when ANY of the seven components holds text — including the two
 * (`state`, `streetSuffix`) that `addressHasContent` deliberately does not
 * count. A block with only those is not blank, and not an address either.
 */
export function addressHasAnyComponent(value: {
  readonly streetNumber?: string | null | undefined;
  readonly streetName?: string | null | undefined;
  readonly streetSuffix?: string | null | undefined;
  readonly addressLine2?: string | null | undefined;
  readonly city?: string | null | undefined;
  readonly state?: string | null | undefined;
  readonly postalCode?: string | null | undefined;
}): boolean {
  return [
    value.streetNumber,
    value.streetName,
    value.streetSuffix,
    value.addressLine2,
    value.city,
    value.state,
    value.postalCode,
  ].some(filled);
}

/** The editable half, refined — what an inline create block on a parent sends. */
export const addressInlineSchema = addressEditableSchema.refine(addressHasContent, {
  message: ADDRESS_NOT_EMPTY,
});

export const addressWriteSchema = addressEditableSchema
  .extend({
    entityType: z.enum(ADDRESS_ENTITY_TYPES),
    entityId: z.uuid(),
  })
  .refine(addressHasContent, { message: ADDRESS_NOT_EMPTY });
export type AddressWrite = z.infer<typeof addressWriteSchema>;

export const addressUpdateSchema = z.object({
  version: z.number().int().positive(),
  changes: partialUpdateSchema(addressEditableSchema),
});

export interface AddressRow {
  readonly id: string;
  readonly entityType: AddressEntityType;
  readonly entityId: string;
  readonly purposeId: string;
  readonly purposeKey: string;
  readonly purposeName: string;
  readonly streetNumber: string | null;
  readonly streetName: string | null;
  readonly streetSuffix: string | null;
  /** Database-composed from the three above. Read-only; never sent on a write. */
  readonly streetLine: string | null;
  readonly addressLine2: string | null;
  readonly city: string | null;
  readonly state: string | null;
  readonly postalCode: string | null;
  readonly attention: string | null;
  readonly isPrimary: boolean;
  readonly status: 'active' | 'inactive';
  readonly notes: string | null;
  readonly version: number;
}

/** What a form holds while it is being typed into — strings, never null. */
export interface AddressFormValues {
  purposeId: string;
  streetNumber: string;
  streetName: string;
  streetSuffix: string;
  addressLine2: string;
  city: string;
  state: string;
  postalCode: string;
  attention: string;
  isPrimary: boolean;
  /**
   * 0040 gave every address a notes column and the card never built the field,
   * so "loading dock entrance, ring the bell" had nowhere to go (docs/11 X-9).
   */
  notes: string;
}

export const EMPTY_ADDRESS_FORM: AddressFormValues = {
  purposeId: '',
  streetNumber: '',
  streetName: '',
  streetSuffix: '',
  addressLine2: '',
  city: '',
  state: '',
  postalCode: '',
  attention: '',
  isPrimary: false,
  notes: '',
};

/**
 * The suffixes offered in the datalist. Not a constraint — anything types — just
 * the dozen that cover almost every Carolinas address, so the common case is two
 * keystrokes and the rare one is still possible.
 */
export const COMMON_STREET_SUFFIXES = [
  'Ave',
  'Blvd',
  'Cir',
  'Ct',
  'Dr',
  'Hwy',
  'Ln',
  'Pkwy',
  'Pl',
  'Rd',
  'St',
  'Ter',
  'Trl',
  'Way',
] as const;

/** Composes the street line client-side, matching 0040's generated column. */
export function composeStreetLine(parts: {
  readonly streetNumber?: string | null;
  readonly streetName?: string | null;
  readonly streetSuffix?: string | null;
}): string {
  return [parts.streetNumber, parts.streetName, parts.streetSuffix]
    .filter(filled)
    .join(' ')
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * One line, for a card or a list row. The single formatter, so a property card
 * and a vendor card cannot word the same address differently.
 */
export function formatAddress(address: {
  readonly streetLine?: string | null;
  readonly streetNumber?: string | null;
  readonly streetName?: string | null;
  readonly streetSuffix?: string | null;
  readonly addressLine2?: string | null;
  readonly city?: string | null;
  readonly state?: string | null;
  readonly postalCode?: string | null;
}): string {
  // Takes the composed line when the caller has one (every server read does)
  // and composes it otherwise, so an unsaved form previews identically.
  const street = filled(address.streetLine) ? address.streetLine : composeStreetLine(address);
  const cityLine = [address.city, address.state].filter(filled).join(', ');
  return [street, address.addressLine2, [cityLine, address.postalCode].filter(filled).join(' ')]
    .filter(filled)
    .join(' · ');
}

/**
 * The address a one-line display shows: the primary if there is one, the first
 * active row otherwise. One function so no two screens pick differently.
 */
export function primaryAddress(rows: readonly AddressRow[]): AddressRow | null {
  const active = rows.filter((row) => row.status === 'active');
  return active.find((row) => row.isPrimary) ?? active[0] ?? null;
}

/**
 * Where a notice actually goes: the notice-purpose row if one exists, the
 * primary otherwise. The fall-back rule from 0039, now a purpose lookup —
 * and still in exactly one place, because a rule that lives in prose is how one
 * screen ends up addressing a notice somewhere another screen says it does not.
 */
export function noticeAddress(rows: readonly AddressRow[]): AddressRow | null {
  const active = rows.filter((row) => row.status === 'active');
  return active.find((row) => row.purposeKey === 'notice') ?? primaryAddress(rows);
}

// ---------------------------------------------------------------------------
// The phone standard
// ---------------------------------------------------------------------------

/**
 * Normalization, stated once so no caller has to guess.
 *
 * Strip every non-digit. Ten digits become ###-###-####. Eleven starting with 1
 * drop the 1 and format the rest. **Anything else keeps the digits it was
 * given** — a four-digit internal line and an international number both still
 * save, because a validator that refused them would push the number into a
 * notes field, which is how a phone number becomes unsearchable.
 *
 * Mirrors 0040's backfill SQL exactly; a DB test asserts the two agree, because
 * two implementations of one rule is how the rule stops being one rule.
 */
export function normalizePhone(input: string): string {
  const digits = input.replace(/\D/g, '');
  if (digits.length === 10) return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
  if (digits.length === 11 && digits.startsWith('1')) {
    const rest = digits.slice(1);
    return `${rest.slice(0, 3)}-${rest.slice(3, 6)}-${rest.slice(6)}`;
  }
  return digits;
}

/**
 * Pulls an embedded extension out of a typed number: "555-0100 x412".
 *
 * People type it that way because for years there was nowhere else to put it,
 * and leaving it in the number makes the searchable digits read 5550100412 — a
 * number that matches nothing.
 */
export function splitExtension(input: string): {
  readonly number: string;
  readonly extension: string | null;
} {
  const match = /(?:x|ext\.?)\s*(\d{1,8})\s*$/i.exec(input);
  if (match === null) return { number: input, extension: null };
  return { number: input.slice(0, match.index).trim(), extension: match[1] ?? null };
}

export const phoneEditableSchema = z.object({
  purposeId: z.uuid(),
  /** Normalized server-side; a caller may send whatever a person typed. */
  number: z.string().trim().min(1).max(60),
  /** Digits only, and its own field: an extension is not part of dialling. */
  extension: z
    .string()
    .trim()
    .regex(/^\d{1,8}$/, 'An extension is up to eight digits')
    .nullish(),
  isPrimary: z.boolean().default(false),
  notes: z.string().trim().max(2000).nullish(),
});

/** The editable half on its own — what an inline create block collects. */
export type PhoneEditable = z.infer<typeof phoneEditableSchema>;

export const phoneWriteSchema = phoneEditableSchema.extend({
  entityType: z.enum(PHONE_ENTITY_TYPES),
  entityId: z.uuid(),
});
export type PhoneWrite = z.infer<typeof phoneWriteSchema>;

export const phoneUpdateSchema = z.object({
  version: z.number().int().positive(),
  changes: partialUpdateSchema(phoneEditableSchema),
});

export interface PhoneRow {
  readonly id: string;
  readonly entityType: PhoneEntityType;
  readonly entityId: string;
  readonly purposeId: string;
  readonly purposeKey: string;
  readonly purposeName: string;
  readonly number: string;
  readonly extension: string | null;
  readonly isPrimary: boolean;
  readonly status: 'active' | 'inactive';
  readonly notes: string | null;
  readonly version: number;
}

export interface PhoneFormValues {
  purposeId: string;
  number: string;
  extension: string;
  isPrimary: boolean;
  /** Same story as the address notes — the column existed, the field did not. */
  notes: string;
}

export const EMPTY_PHONE_FORM: PhoneFormValues = {
  purposeId: '',
  number: '',
  extension: '',
  isPrimary: false,
  notes: '',
};

/** "704-555-0100 ×412" — one rendering, everywhere. */
export function formatPhone(phone: {
  readonly number: string;
  readonly extension?: string | null;
}): string {
  return filled(phone.extension) ? `${phone.number} ×${phone.extension}` : phone.number;
}

export function primaryPhone(rows: readonly PhoneRow[]): PhoneRow | null {
  const active = rows.filter((row) => row.status === 'active');
  return active.find((row) => row.isPrimary) ?? active[0] ?? null;
}

/** A purpose from either taxonomy, as the pickers and read payloads carry it. */
export interface ContactPurpose {
  readonly id: string;
  readonly systemKey: string;
  readonly name: string;
}

function filled(part: string | null | undefined): part is string {
  return part !== null && part !== undefined && part.trim() !== '';
}
